"use client"

import { useState } from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"
import { motion } from "framer-motion"
import axios from "axios"
import { toast } from "react-toastify"
import { API_BASE } from "../config/api"
import {
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material"

const FormContainer = styled(motion.div)`
  background-color: var(--input-bg);
  border-radius: 8px;
  padding: 30px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  border: 1px solid var(--border);
`

const FormTitle = styled.h2`
  margin-bottom: 24px;
  color: var(--primary-text);
  font-size: 1.8rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`

const FormGroup = styled.div`
  margin-bottom: 20px;
`

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: var(--secondary-text);
  font-size: 0.9rem;
`

const InputWrapper = styled.div`
  position: relative;
`

const Input = styled(motion.input)`
  width: 100%;
  padding: 12px 16px;
  background-color: rgba(13, 12, 29, 0.6);
  border: 1px solid var(--border);
  border-radius: 4px;
  color: var(--primary-text);
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--accent-blue);
    box-shadow: 0 0 0 2px rgba(79, 141, 251, 0.3);
  }
  
  &::placeholder {
    color: rgba(176, 176, 176, 0.5);
  }
`

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--secondary-text);
  cursor: pointer;
  
  &:hover {
    color: var(--primary-text);
  }
`

const ErrorMessage = styled.p`
  color: var(--error);
  font-size: 0.8rem;
  margin-top: 5px;
  height: 20px;
`

const SubmitButton = styled(motion.button)`
  width: 100%;
  padding: 12px;
  background-color: var(--accent-blue);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: var(--highlight);
  }
  
  &:disabled {
    background-color: rgba(79, 141, 251, 0.5);
    cursor: not-allowed;
  }
`

const LoginLink = styled.p`
  text-align: center;
  margin-top: 20px;
  color: var(--secondary-text);
  font-size: 0.9rem;
  
  a {
    color: var(--accent-blue);
    font-weight: 500;
    
    &:hover {
      color: var(--highlight);
      text-decoration: underline;
    }
  }
`

const LoadingSpinner = styled(motion.div)`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
`

const Register = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
  })
  const [errors, setErrors] = useState({})
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const validateForm = () => {
    const newErrors = {}

    if (!formData.username) {
      newErrors.username = "Username is required"
    } else if (formData.username.length < 4) {
      newErrors.username = "Username must be at least 4 characters"
    }

    if (!formData.password) {
      newErrors.password = "Password is required"
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters"
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password"
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsLoading(true)

    try {
      const response = await axios.post(`${API_BASE}/register`, {
        username: formData.username,
        password: formData.password,
      })

      if (response.data.message) {
        toast.success("Registration successful! You can now login.")
        // Clear form
        setFormData({
          username: "",
          password: "",
          confirmPassword: "",
        })
      }
    } catch (error) {
      console.error("Registration error:", error)

      if (error.response && error.response.data && error.response.data.detail) {
        toast.error(error.response.data.detail)
      } else {
        toast.error("Registration failed. Please try again.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.3 },
    },
  }

  const inputVariants = {
    focus: { scale: 1.02 },
  }

  const buttonVariants = {
    hover: {
      scale: 1.03,
      transition: {
        yoyo: Number.POSITIVE_INFINITY,
        duration: 0.5,
      },
    },
    tap: { scale: 0.95 },
  }

  const spinnerVariants = {
    animate: {
      rotate: 360,
      transition: {
        loop: Number.POSITIVE_INFINITY,
        ease: "linear",
        duration: 1,
      },
    },
  }

  return (
    <FormContainer variants={containerVariants} initial="hidden" animate="visible" exit="exit">
      <FormTitle>
        <PersonAddIcon />
        Register
      </FormTitle>

      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="username">Username</Label>
          <InputWrapper>
            <Input
              type="text"
              id="username"
              name="username"
              placeholder="Choose a username"
              value={formData.username}
              onChange={handleChange}
              variants={inputVariants}
              whileFocus="focus"
            />
          </InputWrapper>
          <ErrorMessage>{errors.username}</ErrorMessage>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <InputWrapper>
            <Input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              placeholder="Choose a password"
              value={formData.password}
              onChange={handleChange}
              variants={inputVariants}
              whileFocus="focus"
            />
            <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </PasswordToggle>
          </InputWrapper>
          <ErrorMessage>{errors.password}</ErrorMessage>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <InputWrapper>
            <Input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              variants={inputVariants}
              whileFocus="focus"
            />
            <PasswordToggle type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
              {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </PasswordToggle>
          </InputWrapper>
          <ErrorMessage>{errors.confirmPassword}</ErrorMessage>
        </FormGroup>

        <SubmitButton type="submit" disabled={isLoading} variants={buttonVariants} whileHover="hover" whileTap="tap">
          {isLoading ? (
            <LoadingSpinner variants={spinnerVariants} animate="animate" />
          ) : (
            <>
              <PersonAddIcon fontSize="small" />
              Register
            </>
          )}
        </SubmitButton>
      </form>

      <LoginLink>
        Already have an account? <Link to="/login">Login</Link>
      </LoginLink>
    </FormContainer>
  )
}

export default Register

